class AboutJourneyTimeline extends HTMLElement {
  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#track = this.querySelector('[data-about-years]');
    this.#label = this.querySelector('[data-about-founded-label]');
    this.#year = this.querySelector('[data-about-founded-year]');
    this.#content = this.querySelector('[data-about-founded-content]');
    this.#buttons = Array.from(this.querySelectorAll('[data-about-year]'));

    if (!this.#track || !this.#buttons.length) return;

    this.#buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        if (this.#suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        this.#selectYear(button, true);
      });
    });

    this.#track.addEventListener(
      'scroll',
      () => {
        if (this.#programmaticScroll) return;

        // Live-update the active year while the track is free-scrolling (drag / touch).
        this.#syncActiveFromScroll(false);

        window.clearTimeout(this.#scrollTimer);
        this.#scrollTimer = window.setTimeout(() => {
          if (this.#dragging || this.#programmaticScroll) return;
          this.#snapToNearest(true);
        }, 100);
      },
      { passive: true }
    );

    // Drag on the whole timeline (founded copy, marker, and years). No mouse-wheel year stepping.
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);
    this.#track.addEventListener('keydown', this.#onKeyDown);

    window.addEventListener('resize', this.#onResize, { passive: true });

    // Theme editor: selecting a Timeline year block should show that year's label/content.
    this.#onBlockSelect = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!this.contains(target)) return;

      const item = target.matches('.about-journey__year-item')
        ? target
        : target.closest('.about-journey__year-item');
      if (!item || !this.contains(item)) return;

      const button = item.querySelector('[data-about-year]');
      if (!(button instanceof HTMLElement)) return;

      this.#ready = true;
      this.#selectYear(button, true);
    };
    document.addEventListener('shopify:block:select', this.#onBlockSelect);

    // Theme editor section re-render: refresh button list and re-apply active year.
    this.#onSectionLoad = () => {
      this.#buttons = Array.from(this.querySelectorAll('[data-about-year]'));
      if (!this.#buttons.length) return;
      const active =
        this.#buttons.find((button) => button.classList.contains('is-active')) || this.#buttons[0];
      this.#applyYear(active);
      this.#centerYear(active, false);
    };
    document.addEventListener('shopify:section:load', this.#onSectionLoad);

    const active =
      this.#buttons.find((button) => button.classList.contains('is-active')) || this.#buttons[0];
    // Apply Liquid's active year immediately; center after layout so scroll-sync
    // cannot replace it with whichever year sits under the marker first.
    this.#applyYear(active);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#centerYear(active, false);
        this.#ready = true;
      });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
    if (this.#onBlockSelect) {
      document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    }
    if (this.#onSectionLoad) {
      document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    }
    window.clearTimeout(this.#scrollTimer);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
    this.#track?.removeEventListener('keydown', this.#onKeyDown);
  }

  #initialized = false;

  /** @type {boolean} */
  #ready = false;

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement | null} */
  #label = null;

  /** @type {HTMLElement | null} */
  #year = null;

  /** @type {HTMLElement | null} */
  #content = null;

  /** @type {HTMLElement[]} */
  #buttons = [];

  /** @type {number | undefined} */
  #scrollTimer;

  /** @type {boolean} */
  #programmaticScroll = false;

  /** @type {boolean} */
  #pointerActive = false;

  /** @type {boolean} */
  #dragging = false;

  /** @type {boolean} */
  #didDrag = false;

  /** @type {boolean} */
  #suppressClick = false;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragScrollLeft = 0;

  /** @type {number} */
  #activePointerId = -1;

  /** @type {((event: Event) => void) | null} */
  #onBlockSelect = null;

  /** @type {((event: Event) => void) | null} */
  #onSectionLoad = null;

  #onResize = () => {
    const active =
      this.#buttons.find((button) => button.classList.contains('is-active')) || this.#buttons[0];
    this.#centerYear(active, false);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#stepYear(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#stepYear(1);
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (!this.#track || event.button !== 0) return;
    // Touch already pans via overflow-x; only drag with mouse / pen.
    if (event.pointerType === 'touch') return;

    this.#pointerActive = true;
    this.#dragging = false;
    this.#didDrag = false;
    this.#suppressClick = false;
    this.#dragStartX = event.clientX;
    this.#dragScrollLeft = this.#track.scrollLeft;
    this.#activePointerId = event.pointerId;
    this.#programmaticScroll = false;
    // Do not capture yet — capture before movement would steal year button clicks.
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (!this.#pointerActive || !this.#track || event.pointerId !== this.#activePointerId) return;

    const deltaX = event.clientX - this.#dragStartX;

    // Wait for a real drag before locking buttons / changing cursor — preserves year clicks.
    if (!this.#dragging) {
      if (Math.abs(deltaX) <= 4) return;
      this.#dragging = true;
      this.#didDrag = true;
      this.#track.classList.add('is-dragging');
      this.classList.add('is-dragging');

      try {
        this.setPointerCapture(event.pointerId);
      } catch {
        // Older browsers may not support pointer capture on this element.
      }
    }

    // Free-scroll during drag so #syncActiveFromScroll can update the year live.
    this.#programmaticScroll = false;
    this.#track.scrollLeft = this.#dragScrollLeft - deltaX;
    this.#syncActiveFromScroll(false);
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    if (!this.#track || (this.#activePointerId !== -1 && event.pointerId !== this.#activePointerId)) {
      return;
    }

    const didDrag = this.#didDrag;

    this.#pointerActive = false;
    this.#dragging = false;
    this.#activePointerId = -1;
    this.#track.classList.remove('is-dragging');
    this.classList.remove('is-dragging');

    try {
      if (this.hasPointerCapture?.(event.pointerId)) {
        this.releasePointerCapture(event.pointerId);
      }
    } catch {
      // Ignore release errors when capture was never set.
    }

    if (didDrag) {
      this.#suppressClick = true;
      window.setTimeout(() => {
        this.#suppressClick = false;
      }, 0);
      this.#snapToNearest(true);
    }
  };

  /**
   * Visual left (-1) or right (+1), accounting for RTL.
   * @param {number} visualDirection
   */
  #stepYear(visualDirection) {
    const activeIndex = this.#buttons.findIndex((button) => button.classList.contains('is-active'));
    const index = activeIndex < 0 ? 0 : activeIndex;
    const delta = this.#isRtl() ? -visualDirection : visualDirection;
    const next = this.#buttons[index + delta];
    if (next) this.#selectYear(next, true);
  }

  #isRtl() {
    if (!this.#track) return false;
    return getComputedStyle(this.#track).direction === 'rtl';
  }

  /**
   * @param {HTMLElement} button
   * @param {boolean} smooth
   */
  #selectYear(button, smooth) {
    if (!button) return;
    this.#applyYear(button);
    this.#centerYear(button, smooth);
  }

  /**
   * Update founded copy + active styles without scrolling.
   * @param {HTMLElement} button
   */
  #applyYear(button) {
    if (!button) return;

    this.#buttons.forEach((item) => {
      const isActive = item === button;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-current', isActive ? 'true' : 'false');
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (this.#label) {
      const label = button.dataset.label || '';
      this.#label.textContent = label;
      this.#label.hidden = label.trim() === '';
    }

    if (this.#year) {
      this.#year.textContent = button.dataset.year || '';
    }

    if (this.#content) {
      const template = button.parentElement?.querySelector('[data-about-year-content]');
      const html = template?.innerHTML?.trim() || '';
      this.#content.innerHTML = html;
      this.#content.hidden = html === '';
    }
  }

  /**
   * @param {HTMLElement} button
   * @param {boolean} smooth
   */
  #centerYear(button, smooth) {
    if (!this.#track || !button) return;

    const trackRect = this.#track.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const delta =
      buttonRect.left + buttonRect.width / 2 - (trackRect.left + trackRect.width / 2);

    if (Math.abs(delta) < 0.5) return;

    this.#programmaticScroll = true;
    this.#track.scrollTo({
      left: this.#track.scrollLeft + delta,
      behavior: smooth ? 'smooth' : 'auto',
    });

    window.clearTimeout(this.#scrollTimer);
    this.#scrollTimer = window.setTimeout(() => {
      this.#programmaticScroll = false;
    }, smooth ? 360 : 80);
  }

  /**
   * Find the year closest to the marker (track center).
   * @returns {HTMLElement | null}
   */
  #nearestYear() {
    if (!this.#track || !this.#buttons.length) return null;

    const trackRect = this.#track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;
    let nearest = this.#buttons[0];
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.#buttons.forEach((button) => {
      const buttonRect = button.getBoundingClientRect();
      const center = buttonRect.left + buttonRect.width / 2;
      const distance = Math.abs(center - trackCenter);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = button;
      }
    });

    return nearest;
  }

  /**
   * Keep founded year in sync with whatever sits under the marker.
   * @param {boolean} center
   */
  #syncActiveFromScroll(center) {
    if (!this.#ready) return;

    const nearest = this.#nearestYear();
    if (!nearest) return;

    if (!nearest.classList.contains('is-active')) {
      if (center) {
        this.#selectYear(nearest, false);
      } else {
        this.#applyYear(nearest);
      }
    }
  }

  /**
   * Snap the track so the nearest year sits under the marker.
   * @param {boolean} smooth
   */
  #snapToNearest(smooth) {
    const nearest = this.#nearestYear();
    if (!nearest) return;
    this.#selectYear(nearest, smooth);
  }
}

if (!customElements.get('about-journey-timeline')) {
  customElements.define('about-journey-timeline', AboutJourneyTimeline);
}

class AboutLogosExpand extends HTMLElement {
  #expanded = false;
  #toggle;
  #more;
  #labelMore;
  #labelLess;

  connectedCallback() {
    if (this.#toggle) return;

    this.#toggle = this.querySelector('[data-about-logos-toggle]');
    this.#more = this.querySelector('[data-about-logos-more]');
    this.#labelMore = this.querySelector('[data-about-logos-label-more]');
    this.#labelLess = this.querySelector('[data-about-logos-label-less]');

    if (!this.#toggle || !this.#more) return;

    this.#toggle.addEventListener('click', this.#onToggle);
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('click', this.#onToggle);
  }

  #onToggle = () => {
    this.#expanded = !this.#expanded;
    this.classList.toggle('is-expanded', this.#expanded);
    this.#more.hidden = !this.#expanded;
    this.#toggle.setAttribute('aria-expanded', String(this.#expanded));

    if (this.#labelMore) this.#labelMore.hidden = this.#expanded;
    if (this.#labelLess) this.#labelLess.hidden = !this.#expanded;
  };
}

if (!customElements.get('about-logos-expand')) {
  customElements.define('about-logos-expand', AboutLogosExpand);
}
